import objectTypes from '../../constants/ObjectTypes';
import themeServices from '../../themes/ThemeServices';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import questionReader from '../../store/readHelpers/QuestionReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import tocReader from '../../store/readHelpers/TocReader';
import scorableItemNumberingService from '../../services/questionView/ScorableItemNumberingService';
import visitedQuestionsReader from '../../store/readHelpers/VisitedQuestionsReader';
import questionSelectionService from '../../services/QuestionSelectionService';
import { reportError } from '../../services/ErrorReporting';
import constants from '../../qti/Constants';
import uiReader from '../../store/readHelpers/uiReader';
import customFieldsService from '../../services/CustomFieldsService';
import studentHighlightsService from '../../services/StudentHighlightsService';
import annotationService from '../../services/AnnotationService';
import uiService from '../../services/UIService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../services/a11yIIBScreenReaderService';

const questionDisplaySelector = {
    selectIsA11yV5QuestionClickHandlerEnabled() {
        return a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v5);
    },

    selectCorrectResponsesForQuestion(correctResponse) {
        return settingsReader.showCorrectResponsesIfAvailable() ? correctResponse : undefined;
    },

    selectIsInSectionView(sectionId) {
        return assessmentReader.useVerticalDisplayForSection(sectionId) && sessionReader.isViewMediaScreen();
    },

    selectIsReady(question, contentResponse, isDocument) {
        return !!questionReader.isQuestionReady(question, contentResponse, isDocument);
    },

    selectAdditionalWrapperClassList(ui, isInSectionView, isAllView, isCurrent, sectionHasStimulus,
        isViewMediaPrintOrPdf, useNumberPerInteraction, isPrintBased, isExtendedRenderingActivated, question) {
        const wrapperClassList = ['question-wrapper'];

        if (isInSectionView) {
            wrapperClassList.push('vertical');
        }
        if (isAllView) {
            wrapperClassList.push('all-view');
        }
        if (isCurrent) {
            wrapperClassList.push('current');
        }
        if (useNumberPerInteraction) {
            wrapperClassList.push('numbering-per-interaction');
        }
        if (this.isWideAndLeftAligned(isExtendedRenderingActivated, question, sectionHasStimulus, ui)) {
            wrapperClassList.push('wide-and-left-align');
        }
        if (isViewMediaPrintOrPdf
            && settingsReader.hasSettingValue(sessionReader.printParamNames.INSERT_PAGE_BREAK_BEFORE_QUESTION, true)) {
            wrapperClassList.push('page-break-before');
        }
        if (isPrintBased) {
            wrapperClassList.push('paper-based-question');
            uiService.applyImageFilterForPaperBased();
        }

        return wrapperClassList;
    },

    isWideAndLeftAligned(isExtendedRenderingActivated, question, sectionHasStimulus, ui) {
        const isWordFormationQuestion = questionReader.isWordFormationQuestion(question);
        const isMultipleInteractionsGapMatchQuestion = questionReader.isGapMatchQuestionWithMultipleInteractions(question);
        if (isMultipleInteractionsGapMatchQuestion) return false;
        return !isExtendedRenderingActivated && !sectionHasStimulus && ui.variables.question__wideAndLeftAlign && !isWordFormationQuestion;
    },

    selectAdditionalQuestionClassList(question, shouldShowTitle, ui, isQuestionInPrintMode) {
        const questionClassList = ['question'];

        if (ui.viewSize) {
            questionClassList.push(ui.viewSize);
        }
        if (question.itemBody?.indexOf('genericTwoColumn') > 0) {
            questionClassList.push('genericTwoColumn');
        }
        if (!shouldShowTitle) {
            questionClassList.push('hidden-title');
        }
        if (sessionReader.getPrintParam('noMargins')) {
            questionClassList.push('noMargins');
        }
        if (isQuestionInPrintMode) {
            questionClassList.push('printQuestionLayout');
        }

        if (settingsReader.hasSettingValue(sessionReader.printParamNames.INSERT_PAGE_BREAK_BEFORE_QUESTION, true)) {
            questionClassList.push('with-page-break');
        }

        return questionClassList;
    },

    selectShouldShowPdfPanel(question, viewMedia, context) {
        return !!(question.PDFPanelModel
        && (viewMedia === 'screen' || context === 'thumbnail')
        && sessionReader.getPrintParam('show_pdf_panels', true));
    },

    selectShouldShowManualSelector(contentRevisionId) {
        try {
            return questionSelectionService.isQuestionInSectionWithSelectionEnabled(contentRevisionId);
        } catch (e) {
            reportError('Error reading questionIsInSectionWithSelectionEnabled');
        }
        return false;
    },

    selectShouldShowInteractionFeedback(isDocument, isReadOnly, isViewMediaPrintOrPdf,
        feedback, isContextPreview, isContextViewOwnDelivery) {
        return !isDocument
            && isReadOnly
            && !isViewMediaPrintOrPdf
            && !!feedback
            && (isContextPreview || isContextViewOwnDelivery);
    },

    selectShouldShowModalFeedback(isDocument, isReadOnly, isViewMediaPrintOrPdf,
        feedback, isContextPreview, isContextViewOwnDelivery) {
        return !isDocument
            && isReadOnly
            && !isViewMediaPrintOrPdf
            && !!(feedback?.[constants.modalFeedbackKey])
            && (isContextPreview || isContextViewOwnDelivery);
    },

    selectResponses(isDocument, { responseIdentifiers = [] }) {
        const userResponses = {};
        const choiceSequences = {};

        if (!isDocument) {
            responseIdentifiers.forEach((responseIdentifier) => {
                userResponses[responseIdentifier.identifier] = responseIdentifier.userResponse;
                choiceSequences[responseIdentifier.identifier] = responseIdentifier.choiceSequence;
            });
        }

        return {
            userResponses,
            choiceSequences,
        };
    },

    selectShowQuestionRubricBlock({ category, objectType }, isQuestionRubricBlockShown) {
        return !isQuestionRubricBlockShown
            && category !== objectTypes.CATEGORY.STIMULUS
            && objectType !== constants.interactionType.GAP_MATCH_INTERACTION;
    },

    selectAnnotatableQuestionHandler({ category, contentRevisionId }) {
        if (!studentHighlightsService.isNoteTakingAndHighlightingEnabled()
            || category === objectTypes.CATEGORY.STIMULUS) {
            return undefined;
        }

        return annotationService.setAnnotatingContent
            .bind(annotationService, contentRevisionId, objectTypes.CATEGORY.QUESTION);
    },

    selectCorrectResponses(isReadOnly, isCodeCompiler, correctResponsesForQuestion) {
        return (isReadOnly || isCodeCompiler) ? correctResponsesForQuestion : undefined;
    },

    computeProps(state, ownProps, question) {
        try {
            const stateProps = this.mapStateToProps(state, question);
            const {
                context,
                correctResponsesForQuestion,
                isCurrent,
                itemResult,
                ui,
                viewMedia,
            } = stateProps;

            const {
                isQuestionRubricBlockShown,
                isAllView,
                onReady,
            } = ownProps;

            const { contentItemId, contentRevisionId, number, objectType } = question;
            const sectionId = tocReader.getQuestionLocation(contentRevisionId)?.sectionId;
            const isCodeCompiler = objectType === objectTypes.COMPILED_CODE_EDITOR;
            const isReadOnly = questionReader.questionIsReadOnly(question, itemResult);
            const shouldShowManualSelector = this.selectShouldShowManualSelector(contentRevisionId);

            const feedback = assessmentReader.getFeedbackForQuestion(contentRevisionId);
            const isContextPreview = sessionReader.isContextPreview();
            const isContextViewOwnDelivery = sessionReader.isContextViewOwnDelivery();
            const isDocument = questionReader.isDocument(question);
            const isInSectionView = this.selectIsInSectionView(sectionId);
            const isManualSelectorReadOnly = shouldShowManualSelector && questionSelectionService.selectionIsDisabledForQuestion(contentRevisionId);
            const isPrintBased = sessionReader.isPrintStylePaperBased();
            const isViewMediaPrintOrPdf = sessionReader.isViewMediaPrintOrPdf(viewMedia);
            const section = assessmentReader.getSectionById(sectionId);
            const sectionHasStimulus = !!assessmentReader.getStimulusForSectionId(section);
            const shouldShowTitle = settingsReader.isShowQuestionTitlesEnabled() && !sessionReader.isContextPreviewAudioScript();
            const themeInstance = themeServices.getThemeInstance();
            const useNumberPerInteraction = scorableItemNumberingService.useNumberPerInteraction();
            const questionNumberId = `nr_${contentItemId}_${number}`;
            const scorableItemId = `item_${contentItemId}_${number}`;
            const headerId = `title_${contentItemId}_${number}`;
            const isExtendedRenderingActivated = questionReader.isExtendedRenderingActivated(question);
            const isSplitQuestionView = questionReader.isSplitQuestionView(question);
            const isQuestionInPrintMode = sessionReader.isModeQuestionInPrintView();

            return {
                additionalQuestionClassList: this.selectAdditionalQuestionClassList(question, shouldShowTitle, ui, isQuestionInPrintMode),
                additionalWrapperClassList: this.selectAdditionalWrapperClassList(ui, isInSectionView, !!isAllView, isCurrent,
                    sectionHasStimulus, isViewMediaPrintOrPdf, useNumberPerInteraction, isPrintBased,
                    isExtendedRenderingActivated, question),
                annotatableQuestionHandler: this.selectAnnotatableQuestionHandler(question),
                correctResponses: this.selectCorrectResponses(isReadOnly, isCodeCompiler, correctResponsesForQuestion),
                feedback,
                extendedRenderingActivated: questionReader.isExtendedRenderingActivated(question),
                headerId,
                interactionSupportsRTL: uiReader.interactionSupportsRTL(objectType),
                isContentBlock: objectType === objectTypes.CONTENT_BLOCK,
                isDocument,
                isForm: objectType === objectTypes.FORM,
                isInSectionView,
                isListeningComponent: customFieldsService.isListeningComponent(question),
                isManualSelectorReadOnly,
                isPrintBased,
                isReadOnly,
                isReadOnlyBasedOnManualSelectionSetup: isManualSelectorReadOnly || questionSelectionService.isNotSelected(contentRevisionId),
                isReady: this.selectIsReady(question, itemResult, isDocument),
                isRemoteOralExam: sessionReader.isRemoteOralExam(),
                isViewMediaScreen: sessionReader.isViewMediaScreen(),
                isVisited: visitedQuestionsReader.isQuestionVisited(contentRevisionId),
                isScorableItemRoute: state.currentlyShown?.isScorableItemRoute || false,
                isA11yIterationForIIBScreenReaderEnabled: a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(),
                isA11yV5QuestionClickHandlerEnabled: this.selectIsA11yV5QuestionClickHandlerEnabled(),
                isSplitQuestionView,
                mainQuestionWrapperLabel: themeInstance.getMainQuestionWrapperLabel({ scorableItemId, questionNumberId, headerId }),
                onReady,
                outcomeVariables: isDocument ? null : itemResult?.outcomeVariables,
                question,
                questionNumberId,
                section,
                sectionHasStimulus,
                sectionId,
                sectionQuestionsCount: assessmentReader.getSectionQuestionsCount(sectionId),
                shouldScrollToFirstInteraction: sessionReader.isContextViewGradingQuestionDelivery(),
                shouldShowInteractionFeedback: this.selectShouldShowInteractionFeedback(isDocument, isReadOnly, isViewMediaPrintOrPdf, feedback,
                    isContextPreview, isContextViewOwnDelivery),
                shouldShowManualSelector,
                shouldShowModalFeedback: this.selectShouldShowModalFeedback(isDocument, isReadOnly, isViewMediaPrintOrPdf, feedback,
                    isContextPreview, isContextViewOwnDelivery),
                shouldShowPdfPanel: this.selectShouldShowPdfPanel(question, viewMedia, context),
                shouldShowScantronSketches: !isDocument && settingsReader.areAttachedSketchesEnabled(),
                shouldShowTitle,
                showFlagging: settingsReader.isFlaggingEnabled() && sessionReader.isInteractive(),
                showQuestionRubricBlock: this.selectShowQuestionRubricBlock(question, isQuestionRubricBlockShown),
                showKeyWordListBlock: questionReader.isWordFormationQuestion(question),
                sketchCode: question ? assessmentReader.getQuestionSketchCode(contentRevisionId) : undefined,
                stimulusPosition: assessmentReader.getSectionStimulusPosition(section),
                themeInstance,
                themeQuestionLabel: themeInstance.getThemeQuestionLabel(section, question),
                useNumberPerInteraction,
                ...this.selectResponses(isDocument, itemResult),
                ...stateProps,
            };
        } catch (error) {
            console.error(error);

            return {
                isReady: false,
                question,
            };
        }
    },

    mapStateToProps(state, question) {
        const {
            assessment,
            correctResponses,
            currentlyShown,
            itemFlag: { flaggedItems },
            presignedUrls,
            questionSelection,
            response,
            result,
            session,
            settings,
            stimulusHighlights,
            ui,
        } = state;

        const { contentRevisionId, contentItemId, PDFPanelModel } = question;

        return {
            assessmentRunId: assessment.assessmentRunId,
            context: session.context,
            correctResponsesForQuestion: this.selectCorrectResponsesForQuestion(correctResponses[contentRevisionId]),
            currentScorableItem: currentlyShown.scorableItem,
            flaggedItems,
            hasHighlight: stimulusHighlights.indexOf(contentItemId) !== -1,
            highlightContentItemId: session.requestParameterIds?.highlightContentItemId,
            isCurrent: currentlyShown.questionId === contentRevisionId,
            itemResult: response[contentRevisionId] || {},
            mode: session.mode,
            presignedUrlsForPDFPanel: presignedUrls[PDFPanelModel?.logicalName],
            printParams: session.printParams,
            questionInteractionVisibility: state.interactionVisibility[contentRevisionId],
            questionResult: result?.questions?.[contentRevisionId] || null,
            questionSelection,
            settingValues: settings.values,
            ui,
            viewMedia: session.viewMedia,
            isWordFormationQuestion: questionReader.isWordFormationQuestion(question),
        };
    },
};

export default questionDisplaySelector;
